import React, { forwardRef, KeyboardEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronDownIcon from '@/public/icons/approval/chevron-down.svg';
import { Referrer } from '../types';
import ReferrerChip from './Chip';
import { Caret, ComboboxRoot, InlineInput, InputRow } from './styles';

type P = {
  selected: Referrer[];
  isDeactivated?: (referrer: Referrer) => boolean;
  /** 제거 불가(양식 고정) 참조인 id — 칩 X 미노출 + 백스페이스 삭제 제외 */
  lockedIds?: ReadonlySet<string>;
  onRemove: (id: string) => void;
  keyword: string;
  onKeywordChange: (v: string) => void;
  onFocus: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  open?: boolean;
};

/**
 * 선택 Chip 나열 + 인라인 입력 combobox.
 * - IME 한글 조합 중 백스페이스로 Chip이 삭제되지 않도록 compositionstart/end ref로 가드.
 * - 완전 controlled: 키워드/선택 상태는 상위에서 주입.
 */
const Combobox = forwardRef<HTMLDivElement, P>(function Combobox(
  {
    selected,
    isDeactivated,
    lockedIds,
    onRemove,
    keyword,
    onKeywordChange,
    onFocus,
    onKeyDown,
    placeholder,
    disabled,
    open,
  },
  ref,
) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isComposingRef = useRef(false);
  const [focused, setFocused] = useState(false);

  const focusInput = () => {
    if (!disabled) inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // IME 조합 중에는 백스페이스로 Chip을 삭제하지 않는다.
    if (
      e.key === 'Backspace' &&
      !isComposingRef.current &&
      keyword === '' &&
      selected.length > 0
    ) {
      // 잠긴 칩은 건너뛰고 지울 수 있는 마지막 칩을 지운다.
      const last = [...selected].reverse().find((r) => !lockedIds?.has(r.id));
      if (last) {
        onRemove(last.id);
        e.preventDefault();
      }
    }
    onKeyDown?.(e);
  };

  return (
    <ComboboxRoot
      ref={ref}
      $focused={focused}
      $open={!!open}
      $disabled={disabled}
      onMouseDown={(e) => {
        // Chip X 버튼 / Caret 등 내부 버튼 핸들러는 기본 동작 유지.
        const target = e.target as HTMLElement;
        if (target.closest('button')) return;
        // Chip 이외 영역 클릭 시 input으로 포커스 이동.
        if (target !== inputRef.current) {
          e.preventDefault();
          focusInput();
        }
      }}
      onClick={focusInput}
    >
      {selected.map((r) => (
        <ReferrerChip
          key={r.id}
          referrer={r}
          deactivated={isDeactivated?.(r) ?? false}
          locked={lockedIds?.has(r.id) ?? false}
          onRemove={onRemove}
          disabled={disabled}
        />
      ))}
      <InputRow>
        <InlineInput
          ref={inputRef}
          value={keyword}
          onChange={(e) => onKeywordChange(e.target.value)}
          onFocus={() => {
            setFocused(true);
            onFocus();
          }}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => {
            isComposingRef.current = true;
          }}
          onCompositionEnd={() => {
            isComposingRef.current = false;
          }}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={t('참조인 검색')}
        />
      </InputRow>
      <Caret
        type="button"
        $open={!!open}
        aria-label={open ? t('접기') : t('펼치기')}
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) focusInput();
        }}
      >
        <ChevronDownIcon width={11} height={11} />
      </Caret>
    </ComboboxRoot>
  );
});

export default Combobox;
